// src/components/CopyLinkButton.jsx
//
// Task #9085 — shared record-drawer header action. Copies a canonical,
// absolute record URL. Clipboard failure (denied permission, insecure
// context, missing API) is reported inline with the URL selectable so the
// user can still copy it by hand. Used by Office, Project and Market drawers.
import { useEffect, useRef, useState } from "react";
import { Link2, Check } from "lucide-react";
import { T } from "../app/tokens.js";

/** Pure: absolute URL for an in-app path. */
export function absoluteRecordUrl(path, origin = typeof window === "undefined" ? "" : window.location.origin) {
  if (!path) return "";
  return /^https?:\/\//.test(path) ? path : `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Copy text; resolves true/false and never throws. */
export async function copyText(text) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

export function CopyLinkButton({ path, testId = "record-copy-link", label = "Copy link", compact = false }) {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const timer = useRef(null);
  const url = absoluteRecordUrl(path);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { setState("idle"); }, [path]);

  const onClick = async () => {
    clearTimeout(timer.current);
    const ok = await copyText(url);
    setState(ok ? "copied" : "failed");
    if (ok) timer.current = setTimeout(() => setState("idle"), 2200);
  };

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" data-testid={testId} data-copy-url={url} onClick={onClick}
        aria-label={state === "copied" ? "Link copied" : `${label}: ${url}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: compact ? "0 8px" : "0 10px",
          border: `1px solid ${T.border}`, borderRadius: 8, background: T.card, color: state === "copied" ? T.ragGreenText : T.dark,
          fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
        }}>
        {state === "copied" ? <Check size={13} aria-hidden="true" /> : <Link2 size={13} aria-hidden="true" />}
        {state === "copied" ? "Copied" : label}
      </button>
      <span role="status" aria-live="polite" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        {state === "copied" ? "Link copied to clipboard" : ""}
      </span>
      {state === "failed" && (
        <span role="alert" data-testid={`${testId}-failed`} style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 5, width: 280, boxSizing: "border-box",
          padding: "8px 10px", borderRadius: 8, background: T.card, border: `1px solid ${T.border}`, boxShadow: T.cardShadow,
          fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold,
        }}>
          Couldn't copy to the clipboard. Copy the link manually:
          <input readOnly value={url} data-testid={`${testId}-fallback`} aria-label="Record link"
            onFocus={(e) => e.target.select()}
            style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 6, padding: "4px 6px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: T.fsXs, color: T.dark, background: T.tint || T.lightGray }} />
          <button type="button" data-testid={`${testId}-dismiss`} onClick={() => setState("idle")}
            style={{ marginTop: 6, border: 0, background: "transparent", color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", padding: 0 }}>Dismiss</button>
        </span>
      )}
    </span>
  );
}

export default CopyLinkButton;
